import { getWeek, getMonth, getYear, getDate } from 'date-fns';
import { type ChartDataPoint } from 'types/chart';
import { type SurveyBase } from 'types/surveys';
import { formatDateISO, getMonthName } from './date';

// Type guard to validate survey item structure
const isValidSurveyItem = (item: unknown): item is SurveyBase => {
  if (!item || typeof item !== 'object') return false;

  const surveyItem = item as Record<string, unknown>;
  return (
    'completed' in surveyItem &&
    'completedAt' in surveyItem &&
    'totalSurveyScore' in surveyItem &&
    typeof surveyItem.completed === 'boolean' &&
    typeof surveyItem.completedAt === 'string' &&
    typeof surveyItem.totalSurveyScore === 'number'
  );
};

// Create chart data point from survey item
const createChartDataPoint = (item: SurveyBase): ChartDataPoint => {
  const date = new Date(item.completedAt || '');
  const week = getWeek(date, { weekStartsOn: 1 });
  const month = getMonth(date) + 1;
  const year = getYear(date);
  const day = getDate(date);

  return {
    day,
    score: item.totalSurveyScore || 0,
    date: formatDateISO(date),
    week,
    month,
    year,
    label: `${day}/${month}`,
    weekLabel: `Sem ${week} - ${getMonthName(month)}`,
    isWeeklyGroup: false,
  };
};

// Group data by weeks using reduce for better performance
const groupByWeeks = (data: ChartDataPoint[]): ChartDataPoint[] => {
  if (data.length === 0) return [];

  const weekGroups = data.reduce(
    (groups, item) => {
      const weekKey = `${item.year}-W${item.week}`;
      if (!groups[weekKey]) groups[weekKey] = [];
      groups[weekKey].push(item);
      return groups;
    },
    {} as Record<string, ChartDataPoint[]>
  );

  return Object.values(weekGroups).map(weekData => {
    const avgScore = weekData.reduce((sum, item) => sum + item.score, 0) / weekData.length;
    const firstDay = weekData[0];

    return {
      ...firstDay,
      score: Math.round(avgScore * 10) / 10,
      label: firstDay.weekLabel || '',
      isWeeklyGroup: true,
      weekData,
    };
  });
};

// Group data by months using reduce and date utilities
const groupByMonths = (data: ChartDataPoint[]): ChartDataPoint[] => {
  if (data.length === 0) return [];

  const monthGroups = data.reduce(
    (groups, item) => {
      const monthKey = `${item.year}-${item.month}`;
      if (!groups[monthKey]) groups[monthKey] = [];
      groups[monthKey].push(item);
      return groups;
    },
    {} as Record<string, ChartDataPoint[]>
  );

  return Object.values(monthGroups).map(monthData => {
    const avgScore = monthData.reduce((sum, item) => sum + item.score, 0) / monthData.length;
    const firstDay = monthData[0];

    return {
      ...firstDay,
      score: Math.round(avgScore * 10) / 10,
      label: `${firstDay.month} ${firstDay.year}`,
      isMonthlyGroup: true,
      monthData,
    };
  });
};

// Get weeks of a specific month using reduce for better performance
const getWeeksOfMonth = (data: ChartDataPoint[], year: number, month: number): ChartDataPoint[] => {
  const monthData = data.filter(item => item.year === year && item.month === month);
  if (monthData.length === 0) return [];

  const weekGroups = monthData.reduce(
    (groups, item) => {
      const date = new Date(item.date || '');
      const weekOfMonth =
        getWeek(date, { weekStartsOn: 1 }) -
        getWeek(new Date(year, month - 1, 1), { weekStartsOn: 1 }) +
        1;
      const weekKey = `W${weekOfMonth}`;

      if (!groups[weekKey]) groups[weekKey] = [];
      groups[weekKey].push(item);
      return groups;
    },
    {} as Record<string, ChartDataPoint[]>
  );

  return Object.values(weekGroups).map((weekData, index) => {
    const avgScore = weekData.reduce((sum, item) => sum + item.score, 0) / weekData.length;
    const firstDay = weekData[0];

    return {
      ...firstDay,
      score: Math.round(avgScore * 10) / 10,
      label: `Sem ${index + 1}`,
      isWeeklyGroup: true,
      weekData,
    };
  });
};

interface DayScoreData {
  score: number;
  completedAt: string;
}

// Main function to process historical data with optimized filtering
export const processHistoryData = (data: unknown[][]): ChartDataPoint[] => {
  if (!data?.length) return [];

  const scoresByDay = new Map<string, DayScoreData>();

  for (const dayResponses of data) {
    if (!Array.isArray(dayResponses)) continue;

    for (const response of dayResponses) {
      if (isValidSurveyItem(response) && response.completed && response.completedAt) {
        const dateStr = formatDateISO(new Date(response.completedAt));
        const existing = scoresByDay.get(dateStr);
        const score = response.totalSurveyScore ?? 0;

        if (existing) {
          existing.score += score;
        } else {
          scoresByDay.set(dateStr, {
            score,
            completedAt: response.completedAt,
          });
        }
      }
    }
  }

  if (!scoresByDay.size) return [];

  const chartData: ChartDataPoint[] = Array.from(scoresByDay.entries())
    .map(([, { score, completedAt }]) =>
      createChartDataPoint({
        id: '',
        userId: '',
        surveyId: '',
        startedAt: completedAt,
        lastAnsweredAt: completedAt,
        completed: true,
        completedAt,
        isCompleted: true,
        currentQuestionIndex: 0,
        answers: [],
        totalSurveyScore: score,
      })
    )
    .sort((a, b) => {
      const dateA = new Date(a.date || '');
      const dateB = new Date(b.date || '');
      return dateA.getTime() - dateB.getTime();
    });

  return chartData.length > 14 ? groupByWeeks(chartData) : chartData;
};

// Get last N days with data using slice for better performance
export const getLastDaysWithData = (data: ChartDataPoint[], days: number): ChartDataPoint[] =>
  data.length <= days ? data : data.slice(-days);

// Group data by months - reusing internal function
export const groupDataByMonths = (data: ChartDataPoint[]): ChartDataPoint[] => groupByMonths(data);

// Get weeks of a specific month - reusing internal function
export const getWeeksOfMonthData = (
  data: ChartDataPoint[],
  year: number,
  month: number
): ChartDataPoint[] => getWeeksOfMonth(data, year, month);
